import React, { useRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useI18n } from '~Hooks';
import { Locale } from '~Types';
import { BottomLanguage } from '../index';

const LanguageButtons = styled.div`
  display: flex;
  background: #000;
  border-radius: 32px;
  padding: 4px;
  position: relative;
`;

const LanguageIcon = styled.img`
  width: 32px;
  height: 32px;
`;

const LanguageTitle = styled.div`
  padding: 0 0 0 24px;
  color: rgb(255, 255, 255);
  font-size: 16px;
  letter-spacing: 0.24px;
  line-height: 20px;
`;

const LanguageButton = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  min-width: 146px;
  height: 56px;
  text-align: center;
  transition: background 0.3s ease-in-out;
  font-weight: ${(props) => (props.active ? '600' : 'normal')};
  border-radius: 28px;
  padding: 0 16px;
  font-family: ${({ active }) => (active ? 'Eina01-SemiBold' : 'Eina01-Regular')};
  position: relative;
`;

const Active = styled.div<{ offsetLeft: number; width: number }>`
  width: ${({ width }) => `${width}px`};
  height: 56px;
  position: absolute;
  top: 4px;
  background: rgb(33, 33, 36);
  border-radius: 28px;
  transform: translateX(${({ offsetLeft }) => (offsetLeft ? `${offsetLeft}px` : 0)});
  transition: all 0.25s;
`;

interface Props {
  handleChangeLanguage: (lang: Locale) => void;
  languages: BottomLanguage[];
  language: Locale;
}
const LanguagesSelector: React.FunctionComponent<Props> = ({
  handleChangeLanguage,
  languages,
  language,
}) => {
  const { i18n } = useI18n();
  const buttonsRef: any = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [activeWidth, setActiveWidth] = useState(146);
  const [activeOffset, setActiveOffset] = useState<number>(0);

  useEffect(() => {
    const current = buttonsRef.current[language];
    if (current) {
      handleTouch(language);
    }
  }, [buttonsRef]);

  const getSizes = (code: string) => {
    const current = buttonsRef.current[code];
    const { width } = current.getBoundingClientRect();
    const itemPosition = current.offsetLeft - 4;

    return { itemPosition, itemWidth: width };
  };

  const handleTouch = (code: Locale) => {
    const { itemPosition, itemWidth } = getSizes(code);
    setActiveWidth(Math.round(itemWidth));
    setActiveOffset(itemPosition);
    handleChangeLanguage(code);
  };

  return (
    <LanguageButtons>
      <Active offsetLeft={activeOffset} width={activeWidth} />
      {languages.map((lang) => (
        <LanguageButton
          active={language === lang.code}
          ref={(el) => (buttonsRef.current[lang.code] = el)}
          onTouchStart={() => handleTouch(lang.code)}>
          <LanguageIcon src={lang.icon} />
          <LanguageTitle>{i18n(lang.title)}</LanguageTitle>
        </LanguageButton>
      ))}
    </LanguageButtons>
  );
};

export default LanguagesSelector;
